import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Compass, GraduationCap, Users } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { toast } from "sonner";

import { Card } from "~/components/ui/card";
import { cn } from "~/lib/cn";
import { recordIntentAnswerFn } from "~/lib/functions/user-intent";
import type { ForkAnswer } from "~/lib/functions/user-intent";

export const Route = createFileRoute("/_user/welcome")({
  staticData: { title: "Welcome" },
  component: WelcomeFork,
});

// Mirrors the engagement-welcome wizard's sizing: the shell's topbar (h-14)
// plus its vertical padding (py-4 / md:py-6) is subtracted from the viewport
// so `justify-center` centers within the visible area, not past the fold.
const FORK_CONTAINER_CLASS =
  "mx-auto flex min-h-[calc(100dvh-5.5rem)] w-full max-w-2xl flex-col justify-center gap-10 px-4 py-14 md:min-h-[calc(100dvh-6.5rem)]";

interface ForkChoice {
  readonly answer: Exclude<ForkAnswer, "exploring">;
  readonly icon: typeof Users;
  readonly title: string;
  readonly description: string;
}

const FORK_CHOICES: readonly ForkChoice[] = [
  {
    answer: "client",
    icon: Users,
    title: "I'm looking for a coach",
    description: "Find your match and start working toward your goals.",
  },
  {
    answer: "coach",
    icon: GraduationCap,
    title: "I'm a coach",
    description: "Build your practice here and connect with clients.",
  },
];

/**
 * First-run intent fork (U6): the one question new post-launch accounts with
 * unresolved intent answer before anything else. Gated in `_user`'s
 * `beforeLoad` — see that file for the full precondition list (post-launch,
 * no coach record, no deep-link redirect in flight).
 */
function WelcomeFork() {
  const navigate = useNavigate();
  const [pendingAnswer, setPendingAnswer] = useState<ForkAnswer | null>(null);

  const mutation = useMutation({
    mutationFn: (answer: ForkAnswer) =>
      recordIntentAnswerFn({ data: { answer } }),
    onSuccess: (_result, answer) => {
      navigate({ to: answer === "coach" ? "/become-a-coach" : "/dashboard" });
    },
    onError: () => {
      setPendingAnswer(null);
      toast.error("Something went wrong. Please try again.");
    },
  });

  const choose = (answer: ForkAnswer) => {
    if (mutation.isPending) return;
    setPendingAnswer(answer);
    mutation.mutate(answer);
  };

  return (
    <div className={FORK_CONTAINER_CLASS}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-3 text-center"
      >
        <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-purple-50">
          <Compass className="size-6 text-purple-600" />
        </div>
        <h1 className="font-heading text-3xl font-medium tracking-tight text-gray-900">
          What brings you to Dream Coach Match?
        </h1>
        <p className="mx-auto max-w-md text-sm leading-relaxed text-gray-600">
          This helps us show you the right experience from the start.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid gap-4 sm:grid-cols-2"
      >
        {FORK_CHOICES.map((choice) => (
          <button
            key={choice.answer}
            type="button"
            disabled={mutation.isPending}
            onClick={() => choose(choice.answer)}
            className="text-left disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Card
              className={cn(
                "h-full cursor-pointer items-start transition hover:border-purple-200 hover:shadow-md",
                pendingAnswer === choice.answer && "border-purple-300",
              )}
            >
              <div className="flex size-10 items-center justify-center rounded-full bg-purple-50">
                <choice.icon className="size-5 text-purple-600" />
              </div>
              <div className="space-y-1">
                <h2 className="font-medium text-gray-900">{choice.title}</h2>
                <p className="text-sm text-gray-600">{choice.description}</p>
              </div>
            </Card>
          </button>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-center"
      >
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => choose("exploring")}
          className="text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Just exploring for now
        </button>
      </motion.div>
    </div>
  );
}
